import cx from 'classix';
import {
  ChangeEventHandler,
  ForwardedRef,
  FocusEventHandler,
  forwardRef,
  ReactNode,
  useRef,
} from 'react';
import { useObjectRef, useTextField } from 'react-aria';
import { FieldError, Path, RegisterOptions, UseFormRegisterReturn } from 'react-hook-form';
import { Icon, IconKey } from '../atoms';
import { Button } from './Button';
import { Tag } from './Tag';
import { Control, Controller, FieldValues } from 'react-hook-form';

type InputProps = (
  | {
      type: 'inline';
      required?: boolean;
      number?: boolean;
      value: string;
      onChange: ChangeEventHandler<HTMLInputElement>;
      leadIcon?: IconKey;
      tailIcon?: IconKey;
      prefixContent?: string;
      buttonOnPress?: () => void;
      buttonText?: string;
      buttonIcon?: IconKey;
      inputType?: 'text' | 'number' | 'password' | 'email';
    }
  | {
      type: 'multiline';
      value: string;
      required?: boolean;
      onChange: ChangeEventHandler<HTMLTextAreaElement>;
      labelIcon?: IconKey;
    }
  | {
      type: 'tags';
      tags: string[];
      onDeleteTag: (deletedTag: string) => void;
      onAddTag: (addedTag: string) => void;
      labelIcon?: IconKey;
    }
) & {
  error?: string;
  label?: string;
  description?: string;
  disabled?: boolean;
  customWidth?: string;
  placeholder?: string;
  maxLength?: number;
  validate?: (value: string) => string | undefined;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
};

export const Input = (props: InputProps) => {
  if (props.type === 'inline') {
    return <InlineInput {...props} />;
  } else if (props.type === 'multiline') {
    return <MultilineInput {...props} />;
  } else {
    return <TagsInput {...props} />;
  }
};

// TODO: Pick these from InputProps
type InlineInputProps = {
  label?: string;
  leadIcon?: IconKey;
  tailIcon?: IconKey;
  placeholder?: string;
  description?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  prefixContent?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  value?: string;
  buttonOnPress?: () => void;
  buttonText?: string;
  buttonIcon?: IconKey;
  customWidth?: string;
  maxLength?: number;
  validate?: (value: string) => string | undefined;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  inputType?: 'text' | 'number' | 'password' | 'email';
};

const InlineInput = (props: InlineInputProps) => {
  const {
    label,
    placeholder,
    leadIcon,
    required,
    tailIcon,
    error,
    description,
    disabled,
    buttonOnPress,
    buttonText,
    buttonIcon,
    prefixContent,
    onChange,
    value,
    customWidth: customWidth,
    maxLength,
    onFocus,
    onBlur,
    inputType,
  } = { ...props };

  const ref = useRef<HTMLInputElement>(null);
  const { inputProps, isInvalid, validationErrors, errorMessageProps } = useTextField(
    {
      value,
      isRequired: required,
      minLength: required ? 1 : undefined,
      isDisabled: disabled,
      validate: props.validate,
      validationBehavior: 'native',
    },
    ref,
  );

  return (
    <div className={`flex flex-col gap-1.5`}>
      <div className={'text-sm text-gray-500 font-medium'}>{label}</div>
      <div
        className={cx(
          'text-sm text-gray-700 shadow-xs flex flex-row border rounded-lg h-10  focus-within:ring-4',
          buttonText ? 'w-[400px]' : 'w-full',
          error || isInvalid ? 'border-error-100' : '',
          error || isInvalid ? 'ring-error-100' : 'ring-secondary-brand-50',
          disabled ? 'bg-gray-50' : '',
          customWidth,
        )}
      >
        {prefixContent && <div className={`h-full border-r py-2 px-3`}>{prefixContent}</div>}
        <div
          className={`flex flex-row ${prefixContent ? 'py-2 pr-3 w-56' : 'py-2 px-3 w-full'} ${customWidth}`}
        >
          {leadIcon && (
            <div className={'pt-1'}>
              <Icon type={leadIcon} size={'16'} />
            </div>
          )}
          <input
            {...inputProps}
            onFocus={onFocus}
            onBlur={onBlur}
            ref={ref}
            className={cx(
              `w-full py-1 text-sm focus:outline-none ${customWidth}`,
              leadIcon || prefixContent ? 'px-2' : 'pr-2',
              disabled ? 'bg-gray-50' : '',
            )}
            placeholder={placeholder}
            onChange={onChange}
            type={inputType || 'text'}
            value={value}
            maxLength={maxLength}
          />
          {tailIcon && (
            <div className={'pt-1'}>
              <Icon type={tailIcon} size={'16'} />
            </div>
          )}
          {(error || isInvalid) && (
            <div className='pt-1 text-error-500'>
              <Icon type='alert-circle' size={'16'} />
            </div>
          )}
        </div>
        {buttonText && (
          <div className='border-l'>
            <Button
              isDisabled={disabled}
              onPress={buttonOnPress}
              tailIcon={buttonIcon}
              variant='secondary-gray'
              size='sm'
              additionalClassnames={`border-none !shadow-none ${disabled ? 'bg-white' : ''}`}
            >
              {buttonText}
            </Button>
          </div>
        )}
      </div>
      {/* TODO: 'error' is an older approach to doing inline input error messages
       before bringing in React Aria. We need to revisit this to consolidate */}
      {!error && !isInvalid && description && (
        <div className='text-sm text-gray-400'>{description}</div>
      )}
      {error && !isInvalid && <div className='text-sm text-error-500'>{error}</div>}
      {isInvalid && (
        <div {...errorMessageProps} className='text-sm text-error-500'>
          {validationErrors.join(' ')}
        </div>
      )}
    </div>
  );
};

type MultilineInputProps = {
  label?: string;
  tailIcon?: ReactNode;
  error?: string;
  description?: string;
  disabled?: boolean;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  placeholder?: string;
  // display input length out of provided count
  characterCount?: number;
  value: string;
  required?: boolean;
  labelIcon?: IconKey;
  validate?: (value: string) => string | undefined;
};

export const FormsMultilineInput = <FData extends FieldValues>(
  props: Omit<MultilineInputProps, 'onChange' | 'value'> & {
    control: Control<FData>;
    name: Path<FData>;
    rules?: RegisterOptions<FData>;
    formsError?: FieldError;
  },
) => {
  const handleFormsErrors = (error: FieldError | undefined) => {
    if (error?.type === 'maxLength') {
      return `Maximum length exceeded.`;
    } else if (error?.message) {
      return error.message;
    }
  };
  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={props.rules}
      render={({ field }) => (
        <MultilineInput
          {...field}
          label={props.label}
          aria-label={props.label ?? 'Multiline input'}
          placeholder={props.placeholder}
          error={handleFormsErrors(props.formsError)}
          characterCount={props.characterCount}
        />
      )}
    />
  );
};

const MultilineInput = forwardRef(
  (props: MultilineInputProps, ref: ForwardedRef<HTMLTextAreaElement>) => {
    const {
      label,
      description,
      error,
      required,
      disabled,
      placeholder,
      labelIcon,
      onChange,
      characterCount,
      value,
    } = { ...props };

    const textAreaRef = useObjectRef(ref);
    const { inputProps, isInvalid, validationErrors, errorMessageProps } = useTextField(
      {
        value,
        inputElementType: 'textarea',
        isRequired: required,
        minLength: required ? 1 : undefined,
        isDisabled: disabled,
        validate: props.validate,
        validationBehavior: 'native',
      },
      textAreaRef,
    );

    return (
      <div className={'flex flex-col gap-2 min-w-80'}>
        <div className={'text-sm text-gray-500 font-medium flex flex-row justify-between'}>
          {label}
          {labelIcon && <Icon type={labelIcon} size='16' />}
        </div>
        <textarea
          ref={ref}
          {...inputProps}
          required={required}
          disabled={disabled}
          className={`border resize-none rounded-lg text-gray-400 h-28 py-3 px-3.5 disabed:bg-gray-50 focus:outline-none ${error || isInvalid ? 'border-error-100' : ''} focus-within:ring-4 ${disabled ? 'bg-gray-50' : ''} ${error || isInvalid ? 'ring-error-100' : 'ring-secondary-brand-50'} `}
          placeholder={placeholder}
          onChange={onChange}
          value={value}
          aria-label={label ?? 'Multiline input'}
        />
        {!error && characterCount && (
          <div className='text-sm text-gray-400 mt-[2px]'>
            {value?.length ?? 0}/{characterCount} characters
          </div>
        )}
        {!error && !isInvalid && <div className='text-sm text-gray-400'>{description}</div>}
        {error && !isInvalid && <div className='text-sm text-error-500'>{error}</div>}
        {isInvalid && (
          <div {...errorMessageProps} className='text-sm text-error-500'>
            {validationErrors.join(' ')}
          </div>
        )}
      </div>
    );
  },
);

type TagsInputProps = {
  tags: string[];
  label?: string;
  leadIcon?: ReactNode;
  tailIcon?: ReactNode;
  description?: string;
  disabled?: boolean;
  error?: string;
  labelIcon?: IconKey;
  onDeleteTag: (deletedTag: string) => void;
  onAddTag: (addedTag: string) => void;
  placeholder?: string;
};

const TagsInput = (props: TagsInputProps) => {
  const {
    placeholder,
    tags,
    label,
    description,
    error,
    disabled,
    labelIcon,
    onDeleteTag,
    onAddTag,
  } = { ...props };
  const inputRef = useRef<HTMLInputElement>(null);
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && event.target) {
      onAddTag((event.target as HTMLInputElement).value);
      (event.target as HTMLInputElement).value = '';
    }
  };

  const onSelect = () => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  };

  return (
    <div className='flex flex-col gap-1.5 min-w-80'>
      <div className={'text-sm text-gray-500 flex flex-row justify-between'}>
        {label}
        {labelIcon && <Icon type={labelIcon} size='16' />}
      </div>
      <div
        onClick={onSelect}
        className={`border p-1 display-inline rounded-lg overflow-y-scroll text-gray-400 h-28 py-3 px-3.5 text-sm ${disabled ? 'bg-gray-50' : ''} focus-within:border-${error ? 'error-100' : 'secondary-brand-50'} ${error ? 'ring-error-100' : 'ring-secondary-brand-50'} focus-within:ring-4`}
      >
        {tags.map((tag) => {
          return (
            <div key={tag} className='mr-1.5 mb-1 inline-block'>
              <Tag
                size='sm'
                tailIcon='x'
                onTailIconClick={!disabled ? () => onDeleteTag(tag) : undefined}
              >
                {tag}
              </Tag>
            </div>
          );
        })}
        {!disabled && (
          <input
            placeholder={placeholder ?? 'Add tags...'}
            ref={inputRef}
            onKeyDown={onKeyDown}
            type='text'
            className='outline-none p-1'
          />
        )}
      </div>
      {!error && <div className='text-sm text-gray-400'>{description}</div>}
      {error && <div className='text-sm text-error-500'>{error}</div>}
    </div>
  );
};

type InlineFormInputProps = {
  label: string;
  placeholder?: string;
  inputType?: string;
  leadIcon?: IconKey;
  prefixContent?: string;
  customWidth?: string;
  buttonOnPress?: () => void;
  buttonText?: string;
  buttonIcon?: IconKey;
  errors: FieldError | undefined;
  register: () => UseFormRegisterReturn<string>;
  disabled?: boolean;
  autocomplete?: string;
};

export const InlineFormInput = (props: InlineFormInputProps) => {
  const {
    label,
    placeholder,
    leadIcon,
    inputType: type,
    prefixContent,
    customWidth,
    buttonText,
    buttonIcon,
    buttonOnPress,
    register,
    errors,
    autocomplete,
    disabled = false,
  } = { ...props };

  const hasError = !!errors;

  return (
    <div className={`flex flex-col gap-1.5 w-full`}>
      <div className={'text-sm text-gray-500 font-medium'}>{label}</div>
      <div
        className={`text-sm text-gray-400 shadow-xs flex flex-row border rounded-lg ${buttonText ? 'w-full md:w-[400px]' : 'w-full'} h-10 ${hasError ? 'border-error-100' : ''} ${hasError ? 'ring-error-100' : 'ring-secondary-brand-50'} focus-within:ring-4 ${disabled ? 'bg-gray-50' : ''}  ${customWidth}`}
      >
        {prefixContent && <div className={`h-full border-r py-2 px-3`}>{prefixContent}</div>}
        <div
          className={`flex flex-row ${prefixContent ? 'py-2 pr-3 w-56' : 'py-2 px-3 w-full'} ${customWidth}`}
        >
          {leadIcon && (
            <div className={'pt-1'}>
              <Icon type={leadIcon} size={'16'} />
            </div>
          )}
          <input
            className={cx(
              `w-full py-1 text-sm focus:outline-none ${customWidth}`,
              leadIcon || prefixContent ? 'px-2' : 'pr-2',
            )}
            autoComplete={autocomplete}
            placeholder={placeholder}
            type={type || 'text'}
            {...register()}
          />
          {hasError && (
            <div className={'pt-1'}>
              <Icon stroke='error-500' type={'alert-circle'} size={'16'} />
            </div>
          )}
        </div>
        {buttonText && (
          <div className='border-l'>
            <Button
              isDisabled={disabled}
              onPress={buttonOnPress}
              tailIcon={buttonIcon}
              variant='secondary-gray'
              size='sm'
              additionalClassnames={`border-none !shadow-none ${disabled ? 'bg-white' : ''}`}
            >
              {buttonText}
            </Button>
          </div>
        )}
      </div>
      {hasError && (
        <div className='text-sm max-w-[400px] text-error-500 overflow-wrap'>{errors.message}</div>
      )}
    </div>
  );
};
